import type { TFunction } from "i18next";
import {
  CUSTOM_PRESET_ID,
  ROLLING_PRESETS,
  SEASON_PRESETS,
  isCatalogPresetId,
  isValidWindow,
  legacyTokenToPresetId as mapLegacyTokenToPresetId,
  presetIdToLegacyToken,
} from "@nexus/contracts";
import type {
  CatalogPresetId,
  ExplorePresetId,
  ExploreRollingPreset as RollingPreset,
  ExploreSeasonPreset as SeasonPreset,
  RollingPresetId,
  SeasonPresetId,
} from "@nexus/contracts";

import { formatYmd, parseYmdLocal } from "./ymd";

export {
  CUSTOM_PRESET_ID,
  ROLLING_PRESETS,
  SEASON_PRESETS,
  isCatalogPresetId,
  presetIdToLegacyToken,
};
export type { CatalogPresetId, ExplorePresetId, RollingPreset, RollingPresetId, SeasonPreset, SeasonPresetId };

export interface DateWindow {
  start: string;
  end: string;
}

export const DEFAULT_PRESET_ID = "last_90_days";

/** Noun after "vs. prior …" — grammatical, not the formatted previous dates. */
const ROLLING_PRIOR_NOUN: Record<RollingPresetId, string> = {
  last_7_days: "7 days",
  last_14_days: "14 days",
  last_30_days: "30 days",
  last_90_days: "90 days",
  last_year: "year",
};

/**
 * The comparison noun for My Brand card copy (`vs. prior {{previous}}`).
 *
 * Rolling windows use a short span ("90 days"), seasons reuse the picker name
 * ("Halloween"), and a custom range falls back to "period".
 */
export function priorComparisonName(presetId: string, t: TFunction): string {
  if (presetId === CUSTOM_PRESET_ID || !isCatalogPresetId(presetId)) {
    return t("dashboard.kpiCards.priorPeriod", "period");
  }
  const rolling = ROLLING_PRESETS.find((preset) => preset.id === presetId);
  if (rolling) {
    return t(`dashboard.kpiCards.priorPreset.${rolling.id}`, ROLLING_PRIOR_NOUN[rolling.id]);
  }
  const season = SEASON_PRESETS.find((preset) => preset.id === presetId);
  if (season) {
    return t(`dashboard.dateRangePicker.presets.${season.id}`, season.defaultLabel);
  }
  return t("dashboard.kpiCards.priorPeriod", "period");
}

/** The date fields both explore filter objects carry, held per mode. */
export interface ExploreDateSelectionFields {
  startDate: string;
  endDate: string;
  activePresetId: ExplorePresetId;
}

function startOfLocalDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

export function resolveRollingWindow(days: number, today: Date): DateWindow {
  const end = startOfLocalDay(today);
  const start = new Date(end);
  start.setDate(start.getDate() - (days - 1));
  return { start: formatYmd(start), end: formatYmd(end) };
}

/**
 * The occurrence of `season` whose end date is on or before `today`.
 *
 * A boundary-spanning bracket is anchored on its end year, so the New Year
 * bracket ending 15 January resolves its start to the preceding December.
 */
export function resolveSeasonOccurrence(season: SeasonPreset, today: Date): DateWindow {
  const todayLocal = startOfLocalDay(today);

  for (let endYear = todayLocal.getFullYear() + 1; ; endYear -= 1) {
    const end = new Date(endYear, season.endMonth - 1, season.endDay);
    if (end.getTime() > todayLocal.getTime()) continue;
    const startYear = season.spansYearBoundary ? endYear - 1 : endYear;
    const start = new Date(startYear, season.startMonth - 1, season.startDay);
    return { start: formatYmd(start), end: formatYmd(end) };
  }
}

export function resolvePresetWindow(presetId: string, today: Date): DateWindow | null {
  const rolling = ROLLING_PRESETS.find((preset) => preset.id === presetId);
  if (rolling) return resolveRollingWindow(rolling.days, today);
  const season = SEASON_PRESETS.find((preset) => preset.id === presetId);
  if (season) return resolveSeasonOccurrence(season, today);
  return null;
}

export interface ExtraRollingPresetMatch {
  id: string;
  days: number;
  defaultLabel?: string;
}

function seasonWindowEndingIn(season: SeasonPreset, endYear: number): DateWindow {
  const startYear = season.spansYearBoundary ? endYear - 1 : endYear;
  const start = new Date(startYear, season.startMonth - 1, season.startDay);
  const end = new Date(endYear, season.endMonth - 1, season.endDay);
  return { start: formatYmd(start), end: formatYmd(end) };
}

/**
 * Reverse-match a stored YMD pair to a catalog preset. Rolling windows match
 * against `today`; seasons match the occurrence that ends in the stored end year
 * so a Q4 set still reads as Q4 after the season closes.
 */
export function matchPresetIdForWindow(
  startDate: string,
  endDate: string,
  today: Date,
  extraRollingPresets: readonly ExtraRollingPresetMatch[] = [],
): string {
  for (const extra of extraRollingPresets) {
    const window = resolveRollingWindow(extra.days, today);
    if (window.start === startDate && window.end === endDate) return extra.id;
  }
  for (const rolling of ROLLING_PRESETS) {
    const window = resolveRollingWindow(rolling.days, today);
    if (window.start === startDate && window.end === endDate) return rolling.id;
  }
  const end = parseYmdLocal(endDate);
  if (end) {
    const endYear = end.getFullYear();
    for (const season of SEASON_PRESETS) {
      const window = seasonWindowEndingIn(season, endYear);
      if (window.start === startDate && window.end === endDate) return season.id;
    }
  }
  return CUSTOM_PRESET_ID;
}

/** Inverse of `presetIdToLegacyToken`, for URL and saved-preset read paths. */
export function legacyTokenToPresetId(token: string): CatalogPresetId {
  return mapLegacyTokenToPresetId(token) ?? DEFAULT_PRESET_ID;
}

/**
 * Date fields for a filter object that predates them — a stored preset snapshot
 * or a legacy URL, both of which persist only the token.
 *
 * Absolute dates stay empty so the consumer resolves the preset against today
 * rather than a date captured when the snapshot was saved.
 */
export function dateSelectionFromLegacyToken(token: string): ExploreDateSelectionFields {
  return { startDate: "", endDate: "", activePresetId: legacyTokenToPresetId(token) };
}

type SnapshotDateFields = {
  dateRange: string;
  startDate?: string | null;
  endDate?: string | null;
  activePresetId?: string | null;
};

/**
 * Restores picker identity from a stored filter-preset snapshot.
 *
 * `activePresetId` wins when present so 14-day, seasonal, and custom windows
 * survive save/load. Catalog presets keep dates empty and re-resolve against
 * today. Snapshots that only stored a legacy token still map through that token.
 *
 * Accepts `string` picker ids from the wire schema — unknown ids fall through
 * to the leftover `dateRange` token instead of failing the call.
 */
export function withSnapshotDates<T extends SnapshotDateFields>(
  global: T,
): T & ExploreDateSelectionFields {
  const persistedId = global.activePresetId;
  if (persistedId && isCatalogPresetId(persistedId)) {
    return { ...global, startDate: "", endDate: "", activePresetId: persistedId };
  }
  if (persistedId === CUSTOM_PRESET_ID) {
    const startDate = global.startDate ?? "";
    const endDate = global.endDate ?? "";
    if (isValidWindow({ startDate, endDate })) {
      return { ...global, startDate, endDate, activePresetId: CUSTOM_PRESET_ID };
    }
    return { ...global, ...dateSelectionFromLegacyToken(global.dateRange) };
  }
  return { ...global, ...dateSelectionFromLegacyToken(global.dateRange) };
}

export function isKnownPresetId(presetId: string): presetId is ExplorePresetId {
  return presetId === CUSTOM_PRESET_ID || isCatalogPresetId(presetId);
}

/**
 * Canonical date-selection state. Unknown ids, and `custom` without both
 * dates, fall back to the default preset rather than a desynchronized pair.
 *
 * Legacy `dateRange` tokens are not interpreted here — callers keep that field
 * separate and pass it through `dateSelectionFromLegacyToken` when it is the
 * only restore path.
 */
export function normalizeDateSelection(selection: {
  startDate?: string | null;
  endDate?: string | null;
  activePresetId?: string | null;
}): ExploreDateSelectionFields {
  const startDate = selection.startDate ?? "";
  const endDate = selection.endDate ?? "";
  const presetId = selection.activePresetId ?? "";

  if (presetId === CUSTOM_PRESET_ID) {
    if (isValidWindow({ startDate, endDate })) {
      return { startDate, endDate, activePresetId: CUSTOM_PRESET_ID };
    }
    return { startDate: "", endDate: "", activePresetId: DEFAULT_PRESET_ID };
  }

  if (isCatalogPresetId(presetId)) {
    const hasBoth = Boolean(startDate && endDate);
    return {
      startDate: hasBoth ? startDate : "",
      endDate: hasBoth ? endDate : "",
      activePresetId: presetId,
    };
  }

  if (isValidWindow({ startDate, endDate })) {
    return { startDate, endDate, activePresetId: CUSTOM_PRESET_ID };
  }

  return { startDate: "", endDate: "", activePresetId: DEFAULT_PRESET_ID };
}

export interface ExplorePresetOption {
  id: CatalogPresetId;
  label: string;
  secondaryLabel?: string;
  range: { from: Date; to: Date };
}

/**
 * The ordered rail options. Takes `t` as a parameter because a module-scoped
 * function cannot call `useTranslation()`.
 */
export function buildExplorePresets(
  t: TFunction,
  today: Date,
): ExplorePresetOption[] {
  const rolling = ROLLING_PRESETS.map((preset) => {
    const window = resolveRollingWindow(preset.days, today);
    return {
      id: preset.id,
      label: t(`dashboard.dateRangePicker.presets.${preset.id}`, preset.defaultLabel),
      range: toDateRange(window),
    };
  });

  const seasons = SEASON_PRESETS.map((preset) => {
    const window = resolveSeasonOccurrence(preset, today);
    return {
      id: preset.id,
      label: t(`dashboard.dateRangePicker.presets.${preset.id}`, preset.defaultLabel),
      // Start year names the season (Q4 2025), not the January it may close in.
      secondaryLabel: window.start.slice(0, 4),
      range: toDateRange(window),
    };
  });

  return [...rolling, ...seasons];
}

function toDateRange(window: DateWindow): { from: Date; to: Date } {
  // Non-null: both strings were produced by formatYmd above.
  const from = parseYmdLocal(window.start) as Date;
  const to = parseYmdLocal(window.end) as Date;
  return { from, to };
}

